import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QzTrayClientConfig } from "../../../types/qztray/QzTrayClientTypes";

/**
 * ⚙️ Estado global del cliente QZ Tray
 * ------------------------------------------------------------
 * Administra tanto la configuración (backend) como el estado
 * de conexión en tiempo real del cliente local QZ Tray.
 */
export interface QzTrayClientState {
  /** 🧭 Configuración base proveniente del backend */
  config: QzTrayClientConfig | null;

  /** 🌐 Endpoint WebSocket activo (`ws://...` o `wss://...`) */
  endpoint: string | null;

  /** 🔌 Estado actual de conexión con QZ Tray */
  isConnected: boolean;

  /** 🧩 Versión detectada del cliente QZ Tray */
  version: string | null;

  /** ⏳ Estado de carga (peticiones en curso) */
  isLoading: boolean;

  /** ⚙️ Estado de conexión en proceso */
  isConnecting: boolean;

  /** ⚙️ Estado de desconexión en proceso */
  isDisconnecting: boolean;

  /** ⚠️ Mensaje de error (si existe) */
  error: string | null;

  /** 🕓 Última vez que cambió el estado de conexión */
  lastUpdated: string | null;

  /** 🖨️ Nombre de la impresora actualmente seleccionada */
  selectedPrinter: string | null;

  /** 🖨️ Lista detectada de impresoras */
  printers: string[];
}

// ✅ Recuperar impresora persistida (si existe)
const savedPrinter = localStorage.getItem("qzPrinter");

const initialState: QzTrayClientState = {
  config: null,
  endpoint: null,
  isConnected: false,
  version: null,
  isLoading: false,
  isConnecting: false,
  isDisconnecting: false,
  error: null,
  lastUpdated: null,
  selectedPrinter: savedPrinter || null,
  printers: []
};

const qzTrayClientSlice = createSlice({
  name: "qzTrayClient",
  initialState,
  reducers: {
    // ⚙️ Guardar configuración obtenida del backend
    setClientConfig(state, action: PayloadAction<QzTrayClientConfig | null>) {
      state.config = action.payload;
      state.error = null;
    },

    // 🔄 Cambiar estado de carga general
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    // ⚙️ Cambiar estado de conexión en proceso
    setConnecting(state, action: PayloadAction<boolean>) {
      state.isConnecting = action.payload;
      if (action.payload) {
        state.isDisconnecting = false;
      }
    },

    // ⚙️ Cambiar estado de desconexión en proceso
    setDisconnecting(state, action: PayloadAction<boolean>) {
      state.isDisconnecting = action.payload;
      if (action.payload) {
        state.isConnecting = false;
      }
    },

    // ❌ Manejar errores
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    // 🔌 Actualizar estado de conexión real
    setConnectionState: (state, action) => {
      const { isConnected, endpoint } = action.payload;
      state.isConnected = isConnected;
      state.endpoint = endpoint;

      // 💾 Persistir en sessionStorage
      sessionStorage.setItem(
        "qzTrayConnection",
        JSON.stringify({ isConnected, endpoint })
      );
    },

    // ✅ Guardar configuración base
    setConfig: (state, action) => {
      state.config = action.payload;
      sessionStorage.setItem("qzTrayConfig", JSON.stringify(action.payload));
    },

    // 🧩 Actualizar versión detectada
    setClientVersion(state, action: PayloadAction<string | null>) {
      state.version = action.payload;
    },

    // ✅ Guardar impresoras detectadas
    setPrinters(state, action: PayloadAction<string[]>) {
      state.printers = action.payload;
      console.log("Printers",action.payload)
    },

    // ✅ Seleccionar impresora actual y persistir
    setSelectedPrinter(state, action: PayloadAction<string | null>) {
      state.selectedPrinter = action.payload;
      console.log("Printer",action.payload)

      if (action.payload) {
        localStorage.setItem("qzPrinter", action.payload);
      } else {
        localStorage.removeItem("qzPrinter");
      }
    },

    // 🧹 Reiniciar estado completo
    resetQzTrayClientState() {
      // Limpiar persistencia
      localStorage.removeItem("qzPrinter");
      sessionStorage.removeItem("qzTrayConnection");
      sessionStorage.removeItem("qzTrayConfig");

      return initialState;
    },
  },
});

export const {
  setClientConfig,
  setLoading,
  setError,
  setConnectionState,
  setConfig,
  setClientVersion,
  setConnecting,
  setDisconnecting,
  setPrinters,
  setSelectedPrinter,
  resetQzTrayClientState,
} = qzTrayClientSlice.actions;

export default qzTrayClientSlice.reducer;
